/* =============================================================================
 *  attachmentPreview - pure rules for "can this attachment be previewed here,
 *  and how do we keep doing it safely?".
 *
 *  Attachment filenames and content types are attacker-controlled (they come
 *  straight from the sender's message), so every decision here is deliberately
 *  conservative:
 *    - only PNG / JPEG / GIF / WebP images and PDFs are ever previewed;
 *    - SVG (and anything HTML-ish) is download-only - an SVG can carry script;
 *    - a preview re-types the bytes to OUR canonical MIME (`previewMime`), it
 *      never trusts the sender's `text/html` label for a blob it then shows;
 *    - a declared size over 25 MB is never auto-fetched;
 *    - filenames are cleaned for display (bidi overrides, control characters).
 *  No network, no DOM: unit-tested in attachmentPreview.test.ts.
 * ============================================================================= */

export type AttachmentKind = 'image' | 'pdf' | 'other';

/** Never auto-fetch a preview above this (a phone on mobile data, and the
 *  bytes are held in memory for the lifetime of the preview). */
export const PREVIEW_MAX_BYTES = 25 * 1024 * 1024;

export const PDF_MIME = 'application/pdf';

// Maps, not object literals: both keys come from the sender (a content type
// of "constructor" or a file called "x.__proto__" must not resolve through
// Object.prototype to a non-string "mime").
const IMAGE_MIME_BY_TYPE: ReadonlyMap<string, string> = new Map([
  ['image/png', 'image/png'],
  ['image/x-png', 'image/png'],
  ['image/jpeg', 'image/jpeg'],
  ['image/jpg', 'image/jpeg'],
  ['image/pjpeg', 'image/jpeg'],
  ['image/gif', 'image/gif'],
  ['image/webp', 'image/webp'],
]);
const IMAGE_MIME_BY_EXT: ReadonlyMap<string, string> = new Map([
  ['png', 'image/png'],
  ['jpg', 'image/jpeg'],
  ['jpeg', 'image/jpeg'],
  ['jpe', 'image/jpeg'],
  ['jfif', 'image/jpeg'],
  ['gif', 'image/gif'],
  ['webp', 'image/webp'],
]);
const PDF_TYPES = ['application/pdf', 'application/x-pdf'];
// Labels that say "some bytes" - the extension decides then.
const GENERIC_TYPES = [
  '', 'application/octet-stream', 'binary/octet-stream', 'application/x-download',
  'application/download', 'application/force-download', 'application/unknown',
];

/** "image/PNG; name=x.png" -> "image/png". */
export function normalizeContentType(contentType: string | null | undefined): string {
  return String(contentType || '').split(';')[0].trim().toLowerCase();
}

/** Last extension, lowercased, without the dot ("a.pdf.exe" -> "exe"). */
export function fileExtension(filename: string | null | undefined): string {
  const name = String(filename || '').trim().replace(/[\\/]+$/, '');
  const base = name.slice(Math.max(name.lastIndexOf('/'), name.lastIndexOf('\\')) + 1);
  const dot = base.lastIndexOf('.');
  if (dot <= 0 || dot === base.length - 1) return '';
  return base.slice(dot + 1).trim().toLowerCase();
}

/**
 * What kind of attachment is this, for preview purposes?
 * The content type leads; the extension breaks a tie for generic labels and
 * VETOES a mismatch (an "image/png" called photo.html is download-only).
 * SVG is always 'other'.
 */
export function classifyAttachment(
  filename: string | null | undefined,
  contentType: string | null | undefined,
): AttachmentKind {
  const ct = normalizeContentType(contentType);
  const ext = fileExtension(filename);

  if (ext === 'svg' || ext === 'svgz' || ct.indexOf('svg') !== -1) return 'other';

  if (IMAGE_MIME_BY_TYPE.has(ct)) {
    return !ext || IMAGE_MIME_BY_EXT.has(ext) ? 'image' : 'other';
  }
  if (PDF_TYPES.indexOf(ct) !== -1) {
    return !ext || ext === 'pdf' ? 'pdf' : 'other';
  }
  if (GENERIC_TYPES.indexOf(ct) !== -1) {
    if (IMAGE_MIME_BY_EXT.has(ext)) return 'image';
    if (ext === 'pdf') return 'pdf';
  }
  return 'other';
}

/**
 * The canonical MIME a previewed blob is re-typed to, or null when the
 * attachment is not previewable. Derived from the classification, so a blob
 * can only ever be labelled image/png|jpeg|gif|webp or application/pdf.
 */
export function previewMime(
  filename: string | null | undefined,
  contentType: string | null | undefined,
): string | null {
  const kind = classifyAttachment(filename, contentType);
  if (kind === 'pdf') return PDF_MIME;
  if (kind === 'image') {
    return IMAGE_MIME_BY_TYPE.get(normalizeContentType(contentType))
      || IMAGE_MIME_BY_EXT.get(fileExtension(filename))
      || null;
  }
  return null;
}

/** A declared size over the limit (unknown sizes are allowed; the bytes are
 *  checked again once they arrive). */
export function exceedsPreviewLimit(size: number | null | undefined): boolean {
  return typeof size === 'number' && Number.isFinite(size) && size > PREVIEW_MAX_BYTES;
}

export type PreviewVerdict =
  | { ok: true; kind: 'image' | 'pdf'; mime: string }
  | { ok: false; reason: 'unsupported' | 'too-large' | 'empty' };

/** Single gate the UI asks: show a [preview] affordance for this attachment? */
export function previewVerdict(att: {
  filename?: string | null;
  contentType?: string | null;
  size?: number | null;
}): PreviewVerdict {
  const kind = classifyAttachment(att.filename, att.contentType);
  const mime = previewMime(att.filename, att.contentType);
  // The typeof check restates the invariant at the trust boundary: whatever
  // the lookups above did, only a real string may ever type a blob.
  if (kind === 'other' || typeof mime !== 'string' || !mime) return { ok: false, reason: 'unsupported' };
  if (att.size === 0) return { ok: false, reason: 'empty' };
  if (exceedsPreviewLimit(att.size)) return { ok: false, reason: 'too-large' };
  return { ok: true, kind, mime };
}

/** Why a preview is not offered, in the reader's terse voice. */
export function previewRefusal(reason: 'unsupported' | 'too-large' | 'empty'): string {
  if (reason === 'too-large') return 'too large to preview - download it instead';
  if (reason === 'empty') return 'the attachment is empty';
  return 'no preview for this file type - download it instead';
}

// Bidi overrides / isolates, zero-width and control characters: used to make
// "invoice<RLO>fdp.exe" look like "invoiceexe.pdf", or to hide text entirely.
// eslint-disable-next-line no-control-regex -- matching control characters is the whole point
const UNSAFE_NAME_CHARS = /[\u0000-\u001f\u007f-\u009f\u00ad\u061c\u180e\u200b-\u200f\u2028-\u202e\u2060-\u206f\ufeff\ufff9-\ufffb]/g;

/**
 * A filename that is safe to SHOW (and to offer as a download name): no path,
 * no control / bidi / zero-width characters, collapsed spaces, bounded length
 * (the extension survives truncation), never empty.
 */
export function safeFilename(name: string | null | undefined, maxLen = 80): string {
  let s = String(name || '').replace(UNSAFE_NAME_CHARS, ' ');
  s = s.slice(Math.max(s.lastIndexOf('/'), s.lastIndexOf('\\')) + 1);
  s = s.replace(/\s+/g, ' ').trim();
  s = s.replace(/^\.+/, '').trim();
  if (!s) return 'attachment';
  if (s.length <= maxLen) return s;
  const ext = fileExtension(s);
  const tail = ext && ext.length <= 10 ? `.${ext}` : '';
  const keep = Math.max(1, maxLen - tail.length - 3);
  return `${s.slice(0, keep)}...${tail}`;
}

/** Only https: URLs are ever shown in a frame-less <img> or opened in a tab. */
export function isHttpsUrl(u: unknown): u is string {
  if (typeof u !== 'string' || !u) return false;
  try {
    return new URL(u).protocol === 'https:';
  } catch {
    return false;
  }
}
